import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, errMsg, input, money } from './costingApi';

const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({
  kind: 'vehicle', purchase_date: today(), supplier: '', registration: '', vin: '', make: 'Nissan', model: 'NP200',
  engine_type: 'petrol', purchase_price: '', transport_cost: '', auction_fees: '', other_costs: '',
  funding_source: 'jsm_trading', payment_status: 'paid', reference: '', notes: '',
});
const FUNDING = { jsm_trading: 'JSM Trading funding', cash: 'Auto Spares cash', bank: 'Auto Spares bank', payable: 'Supplier credit (payable)', owner: 'Owner contribution' };

const Pick = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label className="text-xs text-slate-300">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={input} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const NewAcquisitionDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState(blank());
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const total = ['purchase_price', 'transport_cost', 'auction_fees', 'other_costs'].reduce((a, k) => a + (parseFloat(f[k]) || 0), 0);
  const field = (k, label, props = {}) => (
    <div><Label className="text-xs text-slate-300">{label}</Label><Input value={f[k]} onChange={set(k)} className={input} data-testid={`acq-${k}`} {...props} /></div>
  );

  const save = async () => {
    setBusy(true);
    try {
      const acq = await api.post('/acquisitions', f);
      toast.success(`${acq.acq_no} recorded as inventory (${money(acq.total_cost)})`);
      setF(blank()); onOpenChange(false); onCreated(acq);
    } catch (e) { toast.error(errMsg(e, 'Could not save')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-white max-h-[92vh] overflow-y-auto" data-testid="new-acquisition-dialog">
        <DialogHeader><DialogTitle>New Stock Acquisition</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Pick label="Type" value={f.kind} onChange={set('kind')} options={{ vehicle: 'Stripping vehicle', batch: 'Parts batch' }} testId="acq-kind" />
          {field('purchase_date', 'Purchase date *', { type: 'date' })}
          {field('supplier', 'Supplier / seller')}
          {f.kind === 'vehicle' && <>
            {field('make', 'Make *')}{field('model', 'Model *')}
            <Pick label="Engine" value={f.engine_type} onChange={set('engine_type')} options={{ petrol: 'Petrol', diesel: 'Diesel', other: 'Other' }} testId="acq-engine" />
            {field('registration', 'Registration')}{field('vin', 'VIN')}
          </>}
          {field('purchase_price', 'Purchase price (R) *', { type: 'number', step: '0.01' })}
          {field('transport_cost', 'Transport (R)', { type: 'number', step: '0.01' })}
          {field('auction_fees', 'Auction / extra fees (R)', { type: 'number', step: '0.01' })}
          {field('other_costs', 'Other direct costs (R)', { type: 'number', step: '0.01' })}
          <Pick label="Funding source" value={f.funding_source} onChange={set('funding_source')} options={FUNDING} testId="acq-funding" />
          <Pick label="Payment status" value={f.payment_status} onChange={set('payment_status')} options={{ paid: 'Paid', partial: 'Partially paid', unpaid: 'Unpaid' }} testId="acq-payment-status" />
          {field('reference', 'Reference / invoice no.')}
          <div className="md:col-span-2">{field('notes', 'Notes')}</div>
        </div>
        <p className="text-sm text-slate-300">Total acquisition cost: <b className="text-emerald-400" data-testid="acq-total-preview">{money(total)}</b> — recorded as inventory, not as an expense.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="text-slate-900">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="acq-save-btn">{busy ? 'Saving…' : 'Save acquisition'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
